// Plurals topic: session building for the plural drill, mirroring the Grammar > Plurals cheat
// sheet. Pure (no React) so the selection and sampling rules can be unit-tested on their own.
//
// Prompt: the singular with its article → a blank for the plural phrase ("a casa → ___" →
// "as casas"): the plural article goes with the noun, and adjectives (fácil → fáceis) have none.
// Grading is exact (case-insensitive, accents required), done by the shared drill. There are no
// challenge levels: the cards are the difficulty ramp, easiest first, and all of them start
// selected, so a session draws across them.

import pluralsData from '../../data/plurals.json'
import { shuffle } from './phraseBank'

export type PluralRuleCardId = 'vowel' | 'ao' | 'es' | 'm' | 'l' | 'invariable' | 'irregular'
export type PluralCardId = PluralRuleCardId

export type PluralCard = {
  id: PluralCardId
  label: string
  // The plurals the card teaches, shown beside its name.
  examples: string
  // One line shown beside the correct answer when the learner misses.
  tip: string
  // Optional rows under the tip, one rule each, for a card with too many rules to read in a line.
  tipRows?: string[]
  // Shown on the setup card.
  note?: string
}

export type PluralItem = {
  card: PluralRuleCardId
  // o / a for the gender; empty for the adjectives (fácil, azul), which have no article.
  article: 'o' | 'a' | ''
  singular: string
  plural: string
}

export const PLURAL_CARDS = pluralsData.cards as PluralCard[]
export const PLURAL_ITEMS = pluralsData.items as PluralItem[]

export const PLURALS_SESSION_LENGTH = 10
// Every card starts selected.
export const DEFAULT_PLURAL_CARD_IDS: PluralCardId[] = PLURAL_CARDS.map((card) => card.id)

export function cardOf(id: PluralCardId): PluralCard {
  return PLURAL_CARDS.find((card) => card.id === id) as PluralCard
}

export function togglePluralCard(ids: PluralCardId[], id: PluralCardId): PluralCardId[] {
  return ids.includes(id) ? ids.filter((existing) => existing !== id) : [...ids, id]
}

// The prompts the selected cards hold.
export function pluralPool(ids: PluralCardId[]): PluralItem[] {
  return PLURAL_ITEMS.filter((item) => ids.includes(item.card))
}

export function pluralPromptCount(ids: PluralCardId[]): number {
  return pluralPool(ids).length
}

// The plural article ("as casas", "os lápis"): o → os, a → as.
function pluralArticle(article: PluralItem['article']): string {
  return article === 'o' ? 'os' : article === 'a' ? 'as' : ''
}

export type PluralPrompt = {
  item: PluralItem
  // "a casa →": the singular with its article, then an arrow into the blank.
  beforeWords: string[]
  // The plural phrase with its plural article ("os lápis"); adjectives have no article.
  answer: string
  tip: string
  tipRows: string[] | null
  // What is read aloud once answered: the same phrase.
  spoken: string
}

function toPrompt(item: PluralItem): PluralPrompt {
  const card = cardOf(item.card)
  const phrase = [pluralArticle(item.article), item.plural].filter(Boolean).join(' ')
  return {
    item,
    beforeWords: [...(item.article ? [item.article] : []), item.singular, '→'],
    answer: phrase,
    tip: card.tip,
    tipRows: card.tipRows ?? null,
    spoken: phrase,
  }
}

// One card → a shuffled draw from it. Several cards → a draw that cycles through the cards in a
// random order, so a session covers as many patterns as it can instead of piling up on whichever
// card has the most words.
function draw(pool: PluralItem[], length: number): PluralItem[] {
  const byCard = new Map<PluralRuleCardId, PluralItem[]>()
  for (const item of shuffle(pool)) byCard.set(item.card, [...(byCard.get(item.card) ?? []), item])
  const order = shuffle([...byCard.keys()])
  const out: PluralItem[] = []
  while (out.length < length && byCard.size > 0) {
    for (const card of order) {
      const items = byCard.get(card)
      if (!items) continue
      out.push(items.pop() as PluralItem)
      if (items.length === 0) byCard.delete(card)
      if (out.length === length) break
    }
  }
  return out
}

export function buildPluralsSession(ids: PluralCardId[]): PluralPrompt[] {
  const pool = pluralPool(ids.length > 0 ? ids : DEFAULT_PLURAL_CARD_IDS)
  return shuffle(draw(pool, PLURALS_SESSION_LENGTH)).map(toPrompt)
}
